import React, { useState } from "react";
import styled from "styled-components";
import { ACSS, P } from "@upsolve/ui";

import { MyUpsolveLink } from "../Links/MyUpsolveLink";
import trackComponentAction from "../analytics/trackComponentAction";
import timeToRead from "../Contentful/timeToRead";
import { CTATarget, getCTATrackingTarget, getCTAURL } from "../CTA/getCTAData";
import { ProductSelectorModal } from "../Forms/ProductSelectorModal";

const trackingProps = {
  componentName: "UpsolveTextBio",
  componentVersion: "1",
};

/**
 * UpsolveTextBio
 * - v0: Lots of text. Bankruptcy, community, Forbes, no credit card etc.
 * - v1: Make it look like a paragraph, less like a disclaimer. Rmv bk myths text
 * - v3: not bankruptcy exclusive
 *
 * @component
 * @version 2
 */
const UpsolveTextBio = (props: { wordCount?: number; ctaTarget?: CTATarget; trackingPageContext: any }) => {
  const { ctaTarget = CTATarget.SCREENER } = props;
  const [isTriageOpen, setIsTriageOpen] = useState(false);

  const ctaTargetToCopy: { [key in CTATarget]: { description: any; hyperlinkText: string } } = {
    [CTATarget.SCREENER]: {
      description: (
        <>
          <b>
            {timeToRead(props.wordCount) ? (
              <>
                <u>{timeToRead(props.wordCount)}</u> •{" "}
              </>
            ) : null}
            Upsolve is a nonprofit that helps you get out of debt with education and free debt relief tools, like our
            bankruptcy filing tool.
          </b>
          &nbsp;Think TurboTax for bankruptcy. Get free education, customer support, and community. Featured in Forbes
          4x and funded by institutions like Harvard University so we'll never ask you for a credit card.&nbsp;
        </>
      ),
      hyperlinkText: "Explore our free tool",
    },
    [CTATarget.AJM]: {
      description: (
        <>
          Upsolve now provides <b>free, limited legal assistance for debt collection defense cases in New York.</b>{" "}
          Featured in Forbes 4x and funded by institutions like Harvard University so we'll never ask you for a credit
          card.&nbsp;
        </>
      ),
      hyperlinkText: "Get free help for your debt collection lawsuit.",
    },
    [CTATarget.STUDENT_LOANS]: {
      description: (
        <>
          <b>
            {timeToRead(props.wordCount) ? (
              <>
                <u>{timeToRead(props.wordCount)}</u> •{" "}
              </>
            ) : null}
            Upsolve is a nonprofit that helps you get out of debt with education and free debt relief tools, like our
            bankruptcy filing tool.
          </b>
          &nbsp;Think TurboTax for bankruptcy. Get free education, customer support, and community. Featured in Forbes
          4x and funded by institutions like Harvard University so we'll never ask you for a credit card.&nbsp;
        </>
      ),
      hyperlinkText: "Explore our free tool",
    },
    [CTATarget.DEBT_TRIAGE]: {
      description: (
        <>
          Upsolve is a nonprofit that helps you get out of debt with free debt relief tools and education.&nbsp;
          Featured in Forbes 4x and funded by institutions like Harvard University so we'll never ask you for a credit
          card.&nbsp;
        </>
      ),
      hyperlinkText: "Get debt help.",
    },
  };
  const url = getCTAURL(ctaTarget);
  return (
    <StyledUpsolveTextBio>
      <P>
        {ctaTargetToCopy[ctaTarget].description}
        <MyUpsolveLink
          aria-busy="false"
          href={url}
          onClick={() => {
            trackComponentAction({
              ...trackingProps,
              actionId: `upsolveTextBio.${getCTATrackingTarget(ctaTarget || CTATarget.SCREENER)}`,
              actionMethod: "click",
            });
            if (ctaTarget === CTATarget.DEBT_TRIAGE) setIsTriageOpen(true);
          }}
        >
          <u> {ctaTargetToCopy[ctaTarget].hyperlinkText}</u>
        </MyUpsolveLink>
      </P>
      {isTriageOpen && (
        <ProductSelectorModal trackingPageContext={props.trackingPageContext} onHide={() => setIsTriageOpen(false)} />
      )}
    </StyledUpsolveTextBio>
  );
};

const StyledUpsolveTextBio = styled.span`
  margin: 1em 0;
  label {
    font-size: 11px;
    font-weight: 500;
    color: ${(props) => props.theme.colors.brand[500]};
  }
  p {
    margin: 0;
    font-size: 14px;
    line-height: 150%;
    color: ${(props) => props.theme.colors.gray[700]};
  }
  span {
    ${ACSS}
    text-decoration: underline;
    &:hover {
      cursor: pointer;
    }
  }
  @media (max-width: ${(props) => props.theme.breakpoints[500]}) {
    margin: 1em 0;
  }
`;

export default UpsolveTextBio;
